import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { inquiriesService } from '@/api/services';
import { resolveTurnstileEnabled, resolveTurnstileSiteKey } from '@/utils/turnstile';
import useTurnstileBotCheck from '@/hooks/useTurnstileBotCheck';

export default function useSiteInquiryForm({
  formType,
  defaultValues,
  mapValues,
  action,
}) {
  const form = useForm({ defaultValues });
  const formStartedAtRef = useRef(Date.now());
  const [websiteField, setWebsiteField] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const turnstileEnabled = resolveTurnstileEnabled();
  const turnstileSiteKey = resolveTurnstileSiteKey();
  const showTurnstileConfigWarning = process.env.NODE_ENV !== 'production' && turnstileEnabled && !turnstileSiteKey;
  const turnstile = useTurnstileBotCheck({
    enabled: turnstileEnabled,
    siteKey: turnstileSiteKey,
  });

  const resetStatus = useCallback(() => {
    setStatus('idle');
    setError('');
    setConfirmation(null);
    formStartedAtRef.current = Date.now();
  }, []);

  const onSubmit = form.handleSubmit(async (values) => {
    setStatus('submitting');
    setError('');
    try {
      const mapped = typeof mapValues === 'function' ? mapValues(values) : values;
      const payload = {
        form_type: formType,
        source_path: typeof window !== 'undefined' ? window.location.pathname : '',
        form_started_at: formStartedAtRef.current,
        website: websiteField,
        bot_token: await turnstile.getToken(action || `${formType}_inquiry`),
        ...mapped,
      };
      const res = await inquiriesService.submit(payload);
      if (!res?.data?.success) {
        throw new Error(res?.data?.error || 'Could not send your message.');
      }
      setConfirmation(res.data);
      setStatus('success');
      form.reset(defaultValues);
      setWebsiteField('');
      formStartedAtRef.current = Date.now();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Could not send your message. Please try again.';
      setError(message);
      setStatus('error');
    }
  });

  return {
    control: form.control,
    watch: form.watch,
    onSubmit,
    status,
    error,
    confirmation,
    submitting: status === 'submitting',
    resetStatus,
    websiteField,
    setWebsiteField,
    turnstile,
    showTurnstileConfigWarning,
  };
}
